import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import API from "../../API";
import "../../style/scss/JobIntake.scss";

function normalizeListItem(raw) {
  if (!raw) return null;
  return {
    id: raw.id ?? raw.ID,
    status: raw.status ?? raw.Status,
    sourceUrl: raw.sourceUrl ?? raw.SourceUrl,
    titleHint: raw.titleHint ?? raw.TitleHint,
    sourcePreview: raw.sourcePreview ?? raw.SourcePreview ?? "",
    discoveredFromIntakeID: raw.discoveredFromIntakeID ?? raw.DiscoveredFromIntakeID,
  };
}

export default function JobIntakeList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [browse, setBrowse] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await API.getAsync("JobPostingIntake");
      if (resp?.ok && Array.isArray(resp.jsonData)) {
        setItems(resp.jsonData.map(normalizeListItem).filter(Boolean));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const stored = window.sessionStorage.getItem("JobIntakeBrowseState");
    if (stored) {
      try {
        setBrowse(JSON.parse(stored));
      } catch {
        window.sessionStorage.removeItem("JobIntakeBrowseState");
      }
    }
  }, []);

  const draftQueue = items.filter(
    (i) => i.status === "Draft" && i.discoveredFromIntakeID
  );

  return (
    <div className="job-intake-page">
      <BreadCrumb props={["Admin", "Import job"]} />
      <header className="job-intake-header">
        <h1>Job posting intake</h1>
        <p className="job-intake-hint">
          Open multiple import windows for different sites. List browsing runs in this window with
          human-like delays between pages.
        </p>
        <CustomButton variant="contained" color="primary" onClick={() => navigate("/job-intake/new")}>
          New intake
        </CustomButton>
      </header>

      {browse?.nextPageUrl ? (
        <section className="job-intake-browse-panel" aria-live="polite">
          <h2>Browse in progress</h2>
          <p>
            Page {browse.currentPage ?? "?"} of ~{browse.estimatedTotalPages ?? "?"} — imported{" "}
            {browse.jobsImported ?? 0} jobs (~{browse.estimatedTotalJobs ?? "?"} total).
          </p>
          <p className="job-intake-countdown">
            {browse.countdownSec != null
              ? `Next page in ~${browse.countdownSec}s…`
              : "Ready for next page — use Continue on the detail view or open the parent intake."}
          </p>
          <CustomButton
            variant="outlined"
            onClick={() => {
              window.sessionStorage.removeItem("JobIntakeBrowseState");
              setBrowse(null);
            }}
          >
            Stop browsing
          </CustomButton>
        </section>
      ) : null}

      {draftQueue.length > 0 ? (
        <section className="job-intake-queue-highlight">
          <h2>URL queue ({draftQueue.length})</h2>
          <p>Draft items discovered from list pages — open to fetch and extract.</p>
        </section>
      ) : null}

      {loading ? <p>Loading…</p> : null}
      {!loading && items.length === 0 ? <p>No intakes yet. Start with New intake.</p> : null}

      <ul className="job-intake-list">
        {items.map((item) => (
          <li
            key={item.id}
            className={
              item.status === "Draft" && item.discoveredFromIntakeID
                ? "job-intake-list-item queue-draft"
                : "job-intake-list-item"
            }
          >
            <Link to={`/job-intake/${item.id}`}>
              <span className="job-intake-list-title">{item.titleHint || "Untitled"}</span>
              <span className="job-intake-list-meta">
                {item.status}
                {item.sourceUrl ? ` · ${item.sourceUrl}` : ""}
              </span>
              <span className="job-intake-list-preview">{item.sourcePreview}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
